import { Component, HostListener, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticatorType } from '@app/constants/authenticator-type';
import { LoginType } from '@app/constants/login-type';
import { MemberRegistrationMode } from '@app/constants/registration';
import { ToastType } from '@app/constants/toast-type';
import { ApiStatus } from '@app/models/api';
import { ListItem } from '@app/models/listItem';
import { ApiService } from '@app/services/api.service';
import { MaskService } from '@app/services/mask.service';
import { ToastService } from '@app/services/toast.service';
import { MaskType } from 'src/app/constants/masks';
import { BasePaths, MemberPaths } from 'src/app/constants/routes';
import { Authenticator, AuthenticatorInfo, AuthenticatorRequest } from 'src/app/models/authenticator';
import { BaseMemberSession, MemberAuthorizationResult } from 'src/app/models/member';
import { RegisterCriteria } from '@app/models/register-criteria';
import { Session } from 'src/app/models/session';
import { AuthService } from 'src/app/services/auth.service';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { HelperService } from 'src/app/services/helper.service';
import { SessionService } from 'src/app/services/storage/session.service';

@Component({
    selector: 'mwp-register',
    templateUrl: './register.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegisterComponent implements OnInit {
    @Input() public isFirstResponder: boolean;
    @Input() public IsResetPassword: boolean;

    public loginType = LoginType.Registration;
    public maskType = MaskType;
    public registrationMode = MemberRegistrationMode;
    public mode: MemberRegistrationMode;

    public baseMemberSessionList: BaseMemberSession[];

    public registrationForm: FormGroup;
    public verifyEmailCodeForm: FormGroup;
    public mfaForm: FormGroup;
    public verifyMFAForm: FormGroup;
    public registrationEmailVerifyMessage: string;
    public registrationMFAVerifyMessage: string;

    public authenticatorTypes: ListItem[];

    public firstResponderFieldName: string = "firstResponderField";
    public lastNameName: string = "lastName";
    public reCaptchaTokenId: string = "reCaptchaTokenId";
    public reCaptchaToken: string = "reCaptchaToken";

    public buttonEnabled: boolean;
    public useSecondaryCaptcha: boolean;

    private countdown: number = 45;
    private interval: any;

    private email: string;
    private mfaAuthenticatorRequest: AuthenticatorRequest;

    @HostListener('window:keydown.enter', ['$event']) onEnter(e: Event): void {
        if (e instanceof KeyboardEvent)
            e.preventDefault();

        switch (this.mode) {
            case MemberRegistrationMode.RegistrationForm:
                if (this.registrationForm.valid)
                    this.submitRegistration();
                break;

            case MemberRegistrationMode.EmailValidation:
                if (this.verifyEmailCodeForm.valid)
                    this.submitEmailCode();
                break;

            case MemberRegistrationMode.MFAConfigForm:
                if (this.mfaForm.valid)
                    this.submitMFAInfo();
                break;

            case MemberRegistrationMode.MFAValidation:
                if (this.verifyMFAForm.valid)
                    this.submitMFACode();
                break;
        }
        e.preventDefault();
    }

    constructor(
        private formBuilder: FormBuilder,
        private router: Router,
        private authService: AuthService,
        private sessionService: SessionService,
        private helperService: HelperService,
        private toastService: ToastService,
        private maskService: MaskService,
        private apiService: ApiService
    ) {
        this.useSecondaryCaptcha = this.sessionService.session.operatingMode.useSecondaryCaptcha;
    }

    ngOnInit() {
        this.mode = MemberRegistrationMode.RegistrationForm;

        this.registrationForm = this.formBuilder.group({
            email: [null, [Validators.required, Validators.compose([Validators.compose([FormValidationService.validEmail(true), FormValidationService.validMatchingValue('confirmedEmail', true)])])]],
            confirmedEmail: [null, [Validators.required, Validators.compose([Validators.compose([FormValidationService.validEmail(true), FormValidationService.validMatchingValue('email', true)])])]],
            dateOfBirth: [null, [Validators.required, Validators.compose([FormValidationService.validMaterialDate(true)])]],
            last4SSN: [null, [Validators.required, Validators.compose([FormValidationService.validLast4SSN(true)])]],
            zipCode: [null, Validators.required],
            [this.reCaptchaTokenId]: [null],
            [this.reCaptchaToken]: [null, [Validators.required]],
            [this.firstResponderFieldName]: this.formBuilder.group({
                [this.lastNameName]: [null]
            })
        });

        if (this.isFirstResponder) {
            this.registrationForm.get(this.firstResponderFieldName).get(this.lastNameName).setValidators([Validators.required]);
        }

        this.verifyEmailCodeForm = this.formBuilder.group({
            codeInput: [null, Validators.required],
        });

        this.authenticatorTypes = [
            { text: `SMS / Text Message`, value: AuthenticatorType.S },
            { text: `Phone Call`, value: AuthenticatorType.P },
        ];

        this.mfaForm = this.formBuilder.group({
            verificationType: [null, [Validators.required]],
            phoneNumber: [null, [Validators.required, FormValidationService.validPhoneNumber(true)]]
        });

        this.verifyMFAForm = this.formBuilder.group({
            codeInput: [null, Validators.required],
        });


        this.sessionService.session.isFaqActive = false;
        this.sessionService.setSession();

        this.apiService.$ApiStatus.subscribe((apiStatus: ApiStatus) => {
            if (this.mode != null && this.mode != undefined && this.mode !== this.registrationMode.RegistrationForm) {
                if (!apiStatus.authToken) {
                    this.verifyMFAForm.reset();
                    this.mfaForm.reset();
                    this.verifyEmailCodeForm.reset();
                    this.registrationForm.reset();
                }
            }
        });
    }

    get firstResponderFormGroup(): FormGroup {
        return this.registrationForm?.get(this.firstResponderFieldName) as FormGroup;
    }

    public redirectToMemberPage(): void {
        this.router.navigateByUrl(BasePaths.Members);
    }

    public submitRegistration(): void {
        // Security: Simple Bot bypass form validation prevention
        if (!this.registrationForm.get(this.reCaptchaToken).value)
            return;

        const l_RegisterCriteria = this.helperService.convertFormGroupIntoObject(this.registrationForm, new RegisterCriteria());
        if (this.isFirstResponder) {
            l_RegisterCriteria.lastName = this.registrationForm.get(this.firstResponderFieldName).get(this.lastNameName).value;
            l_RegisterCriteria.comCode = this.sessionService.session.firstResponderComCode;
        }

        l_RegisterCriteria.reCaptchaID = this.registrationForm.get(this.reCaptchaTokenId).value;
        l_RegisterCriteria.reCaptchaToken = this.registrationForm.get(this.reCaptchaToken).value;

        this.email = this.registrationForm.get('confirmedEmail').value;

        this.authService.registerMember(l_RegisterCriteria, this.IsResetPassword).subscribe((response: AuthenticatorInfo) => {
            this.registrationEmailVerifyMessage = response.message;
            this.mode = MemberRegistrationMode.EmailValidation;
            this.startTimer();
        });
    }

    /**
     * MFA Is Turned off Members
     */
    public submitEmailCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.verifyCode = this.verifyEmailCodeForm.get('codeInput').value;
        l_AuthenticatorRequest.email = this.email;
        this.authService.registerVerifyEmail(l_AuthenticatorRequest, this.IsResetPassword).subscribe((response: Authenticator) => {
            if (response.isCodeVerified) {
                this.toastService.buildToastMessage(ToastType.success, "Email Verified!", "");
                this.mode = MemberRegistrationMode.CreatePassword;
            } else {
                this.toastService.buildToastMessage(ToastType.error, "Code Failed!", "Double check your code or click resend your code for a new one");
            }
        });
    }

    public resendCode(): void {
        if (this.mode == MemberRegistrationMode.EmailValidation) {
            const l_AuthenticatorRequest = new AuthenticatorRequest();
            l_AuthenticatorRequest.email = this.email;
            this.authService.registerResendEmailCode(l_AuthenticatorRequest, this.IsResetPassword).subscribe((response: Authenticator) => {
                this.startTimer();
                if (response.isVerificationSent)
                    this.toastService.buildToastMessage(ToastType.warning, "Email Code Sent", "Check your email in a few minutes!");
                else
                    this.toastService.buildToastMessage(ToastType.warning, "Email Code Failue", "Contact Your Administator For Help!");
            });
        } else {
            this.authService.registerMFA(this.mfaAuthenticatorRequest, this.IsResetPassword).subscribe((response: Authenticator) => {
                this.startTimer();
                if (response.isVerificationSent) {
                    this.toastService.buildToastMessage(ToastType.warning, "SMS/Phone Code Sent", "Wait for a SMS/Text or Phone Call for your code!");
                } else {
                    this.toastService.buildToastMessage(ToastType.error, "SMS/Phone Code Error!", response.returnMessage);
                }
            });
        }
    }

    public submitMFAInfo(): void {
        this.mfaAuthenticatorRequest = new AuthenticatorRequest();
        this.mfaAuthenticatorRequest.phoneNumber = this.maskService.removePhoneMask(this.mfaForm.get('phoneNumber').value);
        this.mfaAuthenticatorRequest.verificationTypeValue = this.helperService.getEnumByKey(AuthenticatorType, this.mfaForm.get('verificationType').value);

        this.authService.registerMFA(this.mfaAuthenticatorRequest, this.IsResetPassword).subscribe((response: Authenticator) => {
            if (response.isVerificationSent) {
                this.registrationMFAVerifyMessage = response.returnMessage;
                this.mode = MemberRegistrationMode.MFAValidation;
                this.startTimer();
            } else {
                this.toastService.buildToastMessage(ToastType.error, "SMS/Phone Code Error!", response.returnMessage);
            }
        });
    }

    public changeMFA(): void {
        this.mfaAuthenticatorRequest = new AuthenticatorRequest();
        this.mfaForm.get('phoneNumber').setValue(null);
        this.mfaForm.get('verificationType').setValue(null);
        this.mfaForm.updateValueAndValidity();
        this.mode = MemberRegistrationMode.MFAConfigForm;
    }

    public submitMFACode(): void {
        this.mfaAuthenticatorRequest.verifyCode = this.verifyMFAForm.get('codeInput').value;
        this.authService.registerValidateMFA(this.mfaAuthenticatorRequest, this.IsResetPassword).subscribe((response: Authenticator) => {
            if (response.isCodeVerified) {
                this.toastService.buildToastMessage(ToastType.success, "Cell Phone Verified!", "");
                this.mode = MemberRegistrationMode.CreatePassword;
            } else {
                this.toastService.buildToastMessage(ToastType.error, "Code Failed!", "Double check your code or click resend your code for a new one");
            }
        });
    }

    public submitPassword(data: any): void {
        this.authService.registerPassword(data, this.IsResetPassword).subscribe((response: MemberAuthorizationResult) => {
            if (response.isMemberAutoLoggedIn) {
                this.router.navigateByUrl(MemberPaths.Dashboard);
            } else {
                this.baseMemberSessionList = response.members;
                this.mode = MemberRegistrationMode.MemberSelect;
            }
        });
    }

    public submitSelectMember(event: BaseMemberSession) {
        const l_RegisterCriteria: RegisterCriteria = new RegisterCriteria();
        l_RegisterCriteria.iD = event.id;
        this.authService.registerSelectedMember(l_RegisterCriteria, this.IsResetPassword).subscribe((response: MemberAuthorizationResult) => {
            this.router.navigateByUrl(MemberPaths.Dashboard);
        });
    }

    public closeFAQs() {
        this.sessionService.session.isFaqActive = false;
        this.sessionService.setSession();
    }

    public reloadCaptcha(useSecondary: boolean): void {
        this.useSecondaryCaptcha = useSecondary;
        this.sessionService.session.operatingMode.useSecondaryCaptcha = useSecondary;
        this.sessionService.setSession();
    }

    startTimer(): void {
        this.clearTimer();
        this.countdown = 45;
        this.buttonEnabled = false;

        this.interval = setInterval(() => {
            if (this.countdown > 0) {
                this.countdown--;
            } else {
                this.clearTimer();
                this.buttonEnabled = true;
            }
        }, 1000);
    }

    clearTimer(): void {
        if (this.interval) {
            clearInterval(this.interval);
        }
    }

    getResendButtonText(): string {
        if (this.buttonEnabled) {
            return 'Resend Code';
        } else {
            const minutes = Math.floor(this.countdown / 60);
            const seconds = this.countdown % 60;
            return `Resend Code in ${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        }
    }

}